import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { resolveImage, waLink, renderLinkedText } from "@/lib/content";
import { scrollTo } from "@/components/Navbar";

const MaskedLine = ({ children, delay, italic }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block font-display text-4xl font-medium leading-[1.08] tracking-tight text-[#FAF8F5] sm:text-5xl lg:text-7xl ${
        italic ? "italic text-[#C5A059]" : ""
      }`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { content } = useContent();
  const hero = content.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0" style={{ y: imageY }}>
        <motion.img
          src={resolveImage(hero.image_url)}
          alt={hero.image_alt || "NRI parent care in Chandigarh — elderly Sikh parents together at home"}
          data-testid="hero-background-image"
          className="hero-image-grade h-[120%] w-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/35" />
      <div className="absolute inset-x-0 bottom-0 h-64 bg-[#1C1917]/40 [mask-image:linear-gradient(to_top,black,transparent)]" />

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-40 lg:px-10"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#FAF8F5]/25 bg-black/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FAF8F5]/90 backdrop-blur-md"
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#C5A059]" />
          {hero.badge}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="font-display text-2xl italic tracking-tight text-[#C5A059] sm:text-3xl lg:text-4xl"
          data-testid="hero-promise"
        >
          {hero.promise_line}
        </motion.p>

        <h1 data-testid="hero-headline" className="mt-4">
          {[hero.h1_line1, hero.h1_line2].filter(Boolean).map((line, i) => (
            <MaskedLine key={line} delay={0.9 + i * 0.18} italic={i === 1}>
              {line}
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.5 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#FAF8F5]/85 md:text-lg"
          data-testid="hero-subtext"
        >
          {renderLinkedText(hero.subtext)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("#packages")}
            data-testid="hero-packages-cta"
            className="group rounded-full bg-[#7A2B21] px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#FAF8F5] transition-[background-color,transform] duration-300 hover:bg-[#5C1E16] hover:scale-[1.03] active:scale-[0.98]"
          >
            See Care Packages
          </button>
          <a
            href={waLink(content.business.whatsapp_number, hero.whatsapp_msg)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="inline-flex items-center gap-2 rounded-full border border-[#FAF8F5]/40 px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#FAF8F5] backdrop-blur-sm transition-[background-color,border-color,transform] duration-300 hover:bg-[#FAF8F5]/10 hover:scale-[1.03] active:scale-[0.98]"
          >
            <MessageCircle size={16} />
            WhatsApp Us
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2 }}
          className="mt-16 flex flex-wrap gap-x-12 gap-y-4 border-t border-[#FAF8F5]/15 pt-8"
          data-testid="hero-stats"
        >
          {hero.stats.map((s) => (
            <div key={s} className="flex items-center gap-3">
              <span className="h-px w-8 bg-[#C5A059]" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FAF8F5]/75">
                {s}
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#promise")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden text-[#FAF8F5]/60 transition-colors hover:text-[#C5A059] lg:block"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll down"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="block"
        >
          <ArrowDown size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
};
